import apiClient from "./api";
import { getApiAccessToken } from "./UserService";
import {
  CreateDeductionReleaseRequest,
  DeductionReleaseDetailType,
  DeductionReleaseEligibleObjectType,
  DeductionReleaseListItemType,
  DeductionReleaseMetadataType,
  ReleaseDeductionReleaseRequest,
} from "../types/DeductionRelease";

const baseUrl = "/api/DeductionReleases";

const authConfig = async () => {
  const token = await getApiAccessToken();
  return { headers: { Authorization: `Bearer ${token}` } };
};

export const getDeductionReleases = async (
  contractId: number,
  addressSystemId: number,
  userId: number,
): Promise<DeductionReleaseListItemType[]> => {
  const config = await authConfig();
  const response = await apiClient.get(baseUrl, {
    ...config,
    params: { contractId, addressSystemId, userId },
  });
  return response.data as DeductionReleaseListItemType[];
};

export const getDeductionReleaseMetadata = async (
  contractId: number,
  addressSystemId: number,
  userId: number,
): Promise<DeductionReleaseMetadataType> => {
  const config = await authConfig();
  const response = await apiClient.get(`${baseUrl}/metadata`, {
    ...config,
    params: { contractId, addressSystemId, userId },
  });
  return response.data as DeductionReleaseMetadataType;
};

export const getEligibleObjects = async (
  contractId: number,
  objectType: number,
  addressSystemId: number,
  userId: number,
): Promise<DeductionReleaseEligibleObjectType[]> => {
  const config = await authConfig();
  const response = await apiClient.get(`${baseUrl}/eligible-objects`, {
    ...config,
    params: { contractId, objectType, addressSystemId, userId },
  });
  return response.data as DeductionReleaseEligibleObjectType[];
};

export const getDeductionReleaseDetail = async (
  id: number,
  contractId: number,
  addressSystemId: number,
  userId: number,
): Promise<DeductionReleaseDetailType> => {
  const config = await authConfig();
  const response = await apiClient.get(`${baseUrl}/${id}`, {
    ...config,
    params: { contractId, addressSystemId, userId },
  });
  return response.data as DeductionReleaseDetailType;
};

export const createDeductionRelease = async (
  request: CreateDeductionReleaseRequest,
): Promise<DeductionReleaseListItemType> => {
  const config = await authConfig();
  const response = await apiClient.post(baseUrl, request, config);
  return response.data as DeductionReleaseListItemType;
};

export const signDeductionRelease = async (
  id: number,
  contractId: number,
  addressSystemId: number,
  userId: number,
  pdfBlob: Blob,
  fileName: string,
): Promise<DeductionReleaseListItemType> => {
  const config = await authConfig();
  const form = new FormData();
  form.append("contractId", String(contractId));
  form.append("addressSystemId", String(addressSystemId));
  form.append("userId", String(userId));
  form.append("pdf", pdfBlob, fileName);

  const response = await apiClient.post(`${baseUrl}/${id}/sign`, form, {
    ...config,
    headers: {
      ...config.headers,
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data as DeductionReleaseListItemType;
};

export const getDeductionSigningStatus = async (
  id: number,
  contractId: number,
  addressSystemId: number,
  userId: number,
): Promise<DeductionReleaseListItemType> => {
  const config = await authConfig();
  const response = await apiClient.get(`${baseUrl}/${id}/signing-status`, {
    ...config,
    params: { contractId, addressSystemId, userId },
  });
  return response.data as DeductionReleaseListItemType;
};

export const releaseDeductionRelease = async (
  id: number,
  request: ReleaseDeductionReleaseRequest,
): Promise<void> => {
  const config = await authConfig();
  await apiClient.post(`${baseUrl}/${id}/release`, request, config);
};

export const downloadSignedDeductionRelease = async (
  id: number,
  contractId: number,
  addressSystemId: number,
  userId: number,
): Promise<Blob> => {
  const config = await authConfig();
  const response = await apiClient.get(`${baseUrl}/${id}/download`, {
    ...config,
    params: { contractId, addressSystemId, userId },
    responseType: "blob",
  });
  return response.data as Blob;
};
